import React, { useState, useEffect } from 'react';
import styles from './ParentDashboard.module.css';
import BookSession from './BookSession';
import PurchaseCredits from './PurchaseCredits';
import ParentThemeToggle from './ParentThemeToggle';
import Lightbox from '../Lightbox';
import StreamVideo, { StreamThumb, VideoOverlay } from '../StreamVideo';

// Signed media urls (R2 1 h, Stream 4 h) go stale in an idle tab: drop the
// cached list after this long so the lazy-load effect refetches fresh urls.
const MEDIA_STALE_MS = 45 * 60 * 1000;

const SOURCE_META = {
  square:  { label: 'Square',  bg: '#e6f4ea', fg: '#1e7d40' },
  venmo:   { label: 'Venmo',   bg: '#e8f1fb', fg: '#2074c0' },
  account: { label: 'Account', bg: '#f1ecfb', fg: '#6b3dbf' },
};
function SourceBadge({ source }) {
  if (!source) return <span style={{ color: '#999', fontSize: '0.72rem' }}>—</span>;
  const meta = SOURCE_META[source] || { label: source, bg: '#eee', fg: '#555' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      background: meta.bg, color: meta.fg, fontSize: '0.68rem', fontWeight: 700,
      letterSpacing: '0.04em', textTransform: 'uppercase',
    }}>{meta.label}</span>
  );
}

function fmt(n) { return `$${Number(n).toFixed(2)}`; }
function fmtDate(d) {
  if (!d) return '';
  const s = typeof d === 'string' ? d : d.toISOString();
  return new Date(s.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const LOW = 75;
const VENMO_HANDLE = 'NextGenSportsCO';

function balClass(b) {
  if (b <= 0) return styles.balEmpty;
  if (b < LOW) return styles.balLow;
  return styles.balGood;
}

const METRIC_EMOJI = {
  '20yd_dash': '\u26A1', 'broad_jump': '\uD83E\uDD98', 'med_ball': '\uD83D\uDCA5',
  'pushups': '\uD83D\uDCAA', '40yd_dash': '\uD83D\uDD25', 'vertical': '\uD83D\uDE80',
};
const LOWER_IS_BETTER = new Set(['20yd_dash', '40yd_dash']);

function buildPriorBests(asmList, currentIdx) {
  const older = asmList.filter((_, i) => i > currentIdx);
  const bests = {};
  for (const asm of older) {
    for (const s of (asm.scores || [])) {
      if (s.value == null) continue;
      const val = parseFloat(s.value);
      const key = s.metric_key;
      const lib = LOWER_IS_BETTER.has(key);
      if (!(key in bests)) bests[key] = val;
      else if (lib) bests[key] = Math.min(bests[key], val);
      else bests[key] = Math.max(bests[key], val);
    }
  }
  return bests;
}

const NAV = [
  { key: 'dashboard', label: 'Dashboard', icon: '\uD83D\uDCB0' },
  { key: 'athletes', label: 'Athletes', icon: '\uD83C\uDFC3' },
  { key: 'booking', label: 'Book a Session', icon: '\uD83D\uDCC5' },
  { key: 'credits', label: 'Purchase Credits', icon: '\uD83D\uDCB3' },
  { key: 'transactions', label: 'Transactions', icon: '\uD83D\uDCCB' },
  { key: 'media', label: 'Media', icon: '\uD83C\uDFAC' },
];

export default function ParentDashboard({ user, data, loading, onLogout, onRefresh, authHeader }) {
  // Email/SMS "new media" links point at /parent#media so the portal opens
  // straight to the Media tab (the hash survives the login redirect).
  const [view, setView] = useState(
    () => (typeof window !== 'undefined' && window.location.hash === '#media') ? 'media' : 'dashboard'
  );
  const [expandedAthletes, setExpandedAthletes] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedAthleteId, setSelectedAthleteId] = useState(null);
  const [media, setMedia] = useState(null);
  const [mediaLoadedAt, setMediaLoadedAt] = useState(0);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaSearch, setMediaSearch] = useState('');
  const [lightbox, setLightbox] = useState(null); // { photos, index, caption } | null
  const [player, setPlayer] = useState(null);     // streamUid being played | null
  // The overlay derives its file from the CURRENT list, so a refetch with fresh
  // signed urls flows straight into it.
  const playerFile = player
    ? (media || []).flatMap(p => p.files || []).find(f => f.streamUid === player)
    : null;
  const openPlayer = (f) => {
    if (media !== null && Date.now() - mediaLoadedAt > MEDIA_STALE_MS) setMedia(null); // refetch first
    setPlayer(f.streamUid);
  };
  // Coach contact card — same data the admin edits on Coach Info. The endpoint
  // is public, so we just fetch it directly on mount; failures fall through to
  // an empty object and the card silently renders nothing.
  const [coach, setCoach] = useState(null);
  useEffect(() => {
    fetch('/api/coach-contact')
      .then(r => r.json())
      .then(d => setCoach(d && typeof d === 'object' ? d : {}))
      .catch(() => setCoach({}));
  }, []);

  // Lazy-load media the first time the parent lands on the Dashboard (for the
  // preview tile) or opens the Media tab — whichever comes first.
  useEffect(() => {
    if ((view === 'media' || view === 'dashboard') && media === null) {
      setMediaLoading(true);
      fetch('/api/parent/media', { headers: authHeader() })
        .then(r => r.json())
        .then(d => { setMedia(Array.isArray(d) ? d : []); setMediaLoadedAt(Date.now()); })
        .catch(() => setMedia([]))
        .finally(() => setMediaLoading(false));
    }
  }, [view, media, authHeader]);

  // Tab came back after sitting idle: signed urls may have expired, so drop
  // the cached list and let the effect above refetch it.
  useEffect(() => {
    const onWake = () => {
      if (document.visibilityState === 'hidden') return;
      if (media !== null && Date.now() - mediaLoadedAt > MEDIA_STALE_MS) setMedia(null);
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [media, mediaLoadedAt]);

  // Format raw phone digits into (xxx) xxx-xxxx for display. Idempotent —
  // any pre-formatted value passes through cleanly.
  const fmtPhone = (input) => {
    if (!input) return '';
    const d = String(input).replace(/\D/g, '').slice(0, 10);
    if (d.length === 0) return '';
    if (d.length <= 3) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };
  const hasCoach = coach && (coach.coachName || coach.phone || coach.email || coach.message);

  if (loading || !data) {
    return (
      <div className={styles.page}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid rgba(200,90,26,0.2)', borderTopColor: '#C85A1A', animation: 'spin 0.7s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ fontSize: '0.9rem', color: '#999' }}>Loading your portal...</div>
        </div>
      </div>
    );
  }

  const { client, athletes, transactions } = data;
  const upcomingBookings = data.upcomingBookings || [];
  const balance = parseFloat(client.balance);
  const note = encodeURIComponent('NGS Credit Reload');
  const venmoBase = `https://venmo.com/${VENMO_HANDLE}?txn=pay&note=${note}`;

  // Multi-athlete families default to "All"; single-athlete families always show their one.
  const isAllMode = athletes.length > 1 && (selectedAthleteId === 'all' || selectedAthleteId === null);
  const activeAthleteId = isAllMode ? null : (selectedAthleteId || athletes[0]?.id);
  const activeAthlete = activeAthleteId ? athletes.find(a => a.id === activeAthleteId) : null;
  const latestAssessment = (activeAthlete?.assessments || [])[0];
  const recentTx = transactions.slice(0, 3);
  const lowBalance = balance < LOW;

  // Upcoming filtered by selected athlete (in single-athlete mode). In all-mode
  // (or zero-athlete edge case) we show everything.
  const visibleUpcoming = activeAthleteId
    ? upcomingBookings.filter(b => (b.athletes || []).some(a => a.id === activeAthleteId))
    : upcomingBookings;

  // Media filtered by the athlete picker: all-mode shows everything; a single
  // athlete shows all-client posts plus posts tagged to that athlete.
  const visibleMedia = (media || []).filter(p => {
    if (isAllMode || !activeAthleteId) return true;
    if (p.visibility === 'all_clients') return true;
    return (p.athletes || []).some(a => a.id === activeAthleteId);
  });

  // Split posts by content type for the Videos / Photos sections. A post with
  // mixed media appears in both sections, but each section only shows the
  // files of its own type so the visual grouping stays clean.
  const splitByType = (type) => visibleMedia
    .map(p => ({ ...p, files: (p.files || []).filter(f => f.type === type) }))
    .filter(p => p.files.length > 0);
  const videoPosts = splitByType('video');
  const photoPosts = splitByType('photo');

  // Search reorders the gallery so matching posts come first -- it does NOT
  // affect the "New" slot (which always shows the absolute newest) and it
  // does NOT filter posts out (non-matches still scroll into view).
  const mq = mediaSearch.trim().toLowerCase();
  const matchesQuery = (p) => {
    if (!mq) return true;
    if ((p.caption || '').toLowerCase().includes(mq)) return true;
    if ((p.athletes || []).some(a => (a.name || '').toLowerCase().includes(mq))) return true;
    // Date matches like "may", "may 22", "2026" — parents browse by when, not just by what.
    if (p.created_at) {
      const dateStr = new Date(p.created_at)
        .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        .toLowerCase();
      if (dateStr.includes(mq)) return true;
    }
    return false;
  };
  const reorderForSearch = (posts) => {
    if (!mq) return posts;
    const matches    = posts.filter(matchesQuery);
    const nonMatches = posts.filter(p => !matchesQuery(p));
    return [...matches, ...nonMatches];
  };

  // Single "post body" renderer reused at two sizes -- bigger inside the
  // NEW slot, compact for the right-side gallery cards. Files are rendered
  // inline (video tags get controls so parents can play in place).
  const renderPostBody = (post, small) => (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: small ? 4 : 6 }}>
        {(post.files || []).map((f, i) => (
          f.streamUid ? (
            // Stream video: inline player in the big NEW slot, poster + play
            // badge in compact gallery cards (tap opens the fullscreen player).
            small
              ? <StreamThumb key={i} file={f} style={{ height: 130, borderRadius: 6 }} onClick={() => openPlayer(f)} />
              : <StreamVideo key={i} file={f} maxHeight={480} />
          ) : f.type === 'video' ? (
            <video
              key={i} src={f.url} controls preload="metadata"
              style={{
                width: '100%',
                maxHeight: small ? 130 : 360,
                borderRadius: small ? 6 : 10,
                background: '#000',
              }}
            />
          ) : (
            <img
              key={i} src={f.url} alt={f.filename || 'media'}
              onClick={() => {
                const photos = (post.files || []).filter(x => x.type === 'photo');
                setLightbox({ photos, index: photos.indexOf(f), caption: post.caption });
              }}
              style={{
                width: '100%',
                maxHeight: small ? 130 : 360,
                objectFit: 'contain',
                borderRadius: small ? 6 : 10,
                background: '#000',
                cursor: 'pointer',
              }}
            />
          )
        ))}
      </div>
      {post.caption && (
        <p style={{
          fontSize: small ? '0.78rem' : '0.92rem',
          lineHeight: 1.4,
          marginTop: small ? 6 : 8,
          ...(small ? {
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          } : {}),
        }}>{post.caption}</p>
      )}
      <div style={{
        fontSize: small ? '0.68rem' : '0.74rem',
        color: 'var(--ngs-text-muted)',
        marginTop: small ? 4 : 6,
      }}>
        {!small && post.visibility === 'all_clients' ? 'Shared with all families · ' : ''}
        {fmtDate(post.createdAt)}
      </div>
    </>
  );

  // Render one section (Videos or Photos). New slot is always the absolute
  // newest post; search reorders only the right-side gallery.
  const renderMediaSection = (title, posts) => {
    if (posts.length === 0) return null;
    const [newPost, ...rest] = posts;
    const galleryPosts = reorderForSearch(rest);
    return (
      <div className={styles.mediaSection} key={title}>
        <h3 className={styles.mediaSectionHead}>{title}</h3>
        <div className={styles.mediaSectionRow}>
          <div className={styles.mediaNewSlot}>
            <div className={styles.mediaNewBadge}>New</div>
            {renderPostBody(newPost, false)}
          </div>
          <div className={styles.mediaGallery}>
            {galleryPosts.length === 0 ? (
              <div className={styles.galleryEmpty}>Only the newest — check back soon for more.</div>
            ) : (
              galleryPosts.map(p => {
                const highlighted = mq && matchesQuery(p);
                return (
                  <div
                    key={p.id}
                    className={`${styles.mediaGalleryCard} ${highlighted ? styles.mediaGalleryHit : ''}`}
                  >
                    {renderPostBody(p, true)}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  // Dashboard preview: up to 3 most-recent posts the parent can see (respects
  // the athlete picker via visibleMedia). Click anything → the full Media tab.
  const previewMedia = visibleMedia.slice(0, 3);
  const renderMediaPreview = () => {
    if (previewMedia.length === 0) return null;
    return (
      <>
        <div className={styles.zoneHeading}>
          <span className={styles.zoneLabel}>Recent Media</span>
        </div>
        <div className={styles.mediaPreviewCard}>
          <div className={styles.mediaPreviewRow}>
            {previewMedia.map(p => {
              const first = (p.files || [])[0];
              const extra = (p.files || []).length - 1;
              return (
                <button key={p.id} className={styles.mediaPreviewItem} onClick={() => setView('media')}>
                  <div className={styles.mediaPreviewThumb}>
                    {first && first.streamUid ? (
                      first.ready && first.thumbnailUrl
                        ? <img src={first.thumbnailUrl} alt={first.filename || 'video'} />
                        : <span className={styles.mediaPreviewProcessing}>Processing…</span>
                    ) : first && first.type === 'video' ? (
                      <video src={first.url} muted preload="metadata" />
                    ) : first ? (
                      <img src={first.url} alt={first.filename || 'media'} />
                    ) : null}
                    {first && first.type === 'video' && (!first.streamUid || first.ready) && (
                      <span className={styles.mediaPreviewPlay}>{'▶'}</span>
                    )}
                    {extra > 0 && <span className={styles.mediaPreviewCount}>+{extra}</span>}
                  </div>
                  {p.caption && <div className={styles.mediaPreviewCaption}>{p.caption}</div>}
                  <div className={styles.mediaPreviewDate}>{fmtDate(p.createdAt)}</div>
                </button>
              );
            })}
          </div>
          <button className={styles.mediaPreviewViewAll} onClick={() => setView('media')}>
            View all media &rarr;
          </button>
        </div>
      </>
    );
  };

  const toggleAthlete = (id) => setExpandedAthletes(p => ({ ...p, [id]: !p[id] }));
  const navigate = (key) => { setView(key); setMobileMenuOpen(false); };

  const sidebarContent = (
    <>
      <div className={styles.sidebarLogo}>
        <div role="img" aria-label="Next Gen Sports" className={styles.sidebarLogoImg} />
      </div>
      <nav className={styles.sidebarNav}>
        {NAV.map(n => (
          <button key={n.key}
            className={`${styles.navItem} ${view === n.key ? styles.navItemActive : ''}`}
            onClick={() => navigate(n.key)}>
            <span className={styles.navIcon}>{n.icon}</span>
            {n.label}
          </button>
        ))}
      </nav>
      <div className={styles.sidebarTagline}>Building the Next Generation of Athletes</div>
      <div className={styles.sidebarFooter}>
        <div className={styles.sidebarUser}>{user.displayName || client.name}</div>
        <button className={styles.sidebarLogout} onClick={onLogout}>Sign Out</button>
      </div>
    </>
  );

  return (
    <div className={styles.page}>
      {/* Desktop sidebar */}
      <aside className={styles.sidebar}>{sidebarContent}</aside>

      {/* Mobile header + hamburger */}
      <div className={styles.mobileHeader}>
        <button className={styles.hamburger} onClick={() => setMobileMenuOpen(true)}>&#9776;</button>
        <div role="img" aria-label="Next Gen Sports" className={styles.mobileLogoImg} />
        <div style={{ width: 36 }} />
      </div>

      {/* Mobile overlay */}
      <div className={`${styles.overlay} ${mobileMenuOpen ? styles.overlayOpen : ''}`} onClick={() => setMobileMenuOpen(false)} />
      {mobileMenuOpen && (
        <aside className={`${styles.sidebar} ${styles.sidebarOpen}`}>{sidebarContent}</aside>
      )}

      {/* Main content */}
      <div className={styles.main}>
        {/* Sticky breadcrumb header */}
        <div className={styles.topbar}>
          <div className={styles.topbarLeft}>
            <span className={styles.topbarWelcome}>Welcome {user.displayName || client.name}</span>
            <span className={styles.topbarArrow}>&gt;</span>
            <span className={styles.topbarView}>{NAV.find(n => n.key === view)?.label || 'Dashboard'}</span>
          </div>
          <div className={styles.topbarDate}>
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            <ParentThemeToggle />
          </div>
        </div>

        <div className={(view === 'booking' || view === 'credits') ? styles.contentFull : styles.content}>

          {/* ══ DASHBOARD ══ */}
          {view === 'dashboard' && (
            <>
              {/* ── Athlete header bar ────────────────────────────────────── */}
              {athletes.length > 0 && (
                <div className={styles.athleteHeaderBar}>
                  <div className={styles.athleteHeaderLeft}>
                    <div className={styles.athleteAvatar}>
                      {isAllMode ? '\uD83D\uDC65' : activeAthlete?.name?.charAt(0)}
                    </div>
                    <div>
                      <div className={styles.athleteHeaderName}>
                        {isAllMode ? 'All Athletes' : activeAthlete?.name}
                      </div>
                      <div className={styles.athleteHeaderMeta}>
                        {isAllMode
                          ? `${athletes.length} athletes on this account`
                          : ([activeAthlete?.position, activeAthlete?.team, activeAthlete?.grad_year ? `Class of ${activeAthlete.grad_year}` : null].filter(Boolean).join(' \u00B7 ') || 'Athlete')}
                      </div>
                    </div>
                  </div>
                  {athletes.length > 1 && (
                    <div className={styles.athletePicker}>
                      <button
                        className={`${styles.athletePickerPill} ${isAllMode ? styles.athletePickerActive : ''}`}
                        onClick={() => setSelectedAthleteId('all')}
                      >
                        All
                      </button>
                      {athletes.map(a => (
                        <button
                          key={a.id}
                          className={`${styles.athletePickerPill} ${a.id === activeAthleteId ? styles.athletePickerActive : ''}`}
                          onClick={() => setSelectedAthleteId(a.id)}
                        >
                          {a.name?.split(' ')[0]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 ZONE 1 \u2014 Your Account (athlete-agnostic) \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */}
              <div className={styles.zoneHeading}>
                <span className={styles.zoneLabel}>Your Account</span>
              </div>

              {/* ── Balance hero ──────────────────────────────────────────── */}
              <div className={styles.heroBalance}>
                <div className={styles.heroBalanceLeft}>
                  <div className={styles.balLabel}>Account Balance</div>
                  <div className={`${styles.balValue} ${balClass(balance)}`}>{fmt(balance)}</div>
                  {lowBalance && (
                    <div className={styles.heroWarning}>
                      {balance <= 0
                        ? 'Your account is empty — add credits to book more sessions.'
                        : 'Your balance is running low.'}
                    </div>
                  )}
                </div>
                <div className={styles.heroBalanceRight}>
                  <button className={styles.heroCta} onClick={() => setView('credits')}>
                    Purchase Credits
                    <span className={styles.heroCtaIcon}>{'\uD83D\uDCB3'}</span>
                  </button>
                  <div className={styles.heroVenmoNote}>
                    Or pay with Venmo:
                    <div className={styles.heroVenmoRow}>
                      <a href={`${venmoBase}&amount=150`} target="_blank" rel="noopener noreferrer" className={styles.heroVenmoLink}>$150</a>
                      <a href={`${venmoBase}&amount=300`} target="_blank" rel="noopener noreferrer" className={styles.heroVenmoLink}>$300</a>
                      <a href={`${venmoBase}&amount=600`} target="_blank" rel="noopener noreferrer" className={styles.heroVenmoLink}>$600</a>
                      <a href={venmoBase} target="_blank" rel="noopener noreferrer" className={styles.heroVenmoLink}>Custom</a>
                    </div>
                    {/* Venmo isn't webhooked into the portal the way Square is — the
                      * coach manually marks the payment received before the balance
                      * updates. This sets that expectation inline so a parent who
                      * just paid via Venmo doesn't panic when their balance hasn't
                      * moved on the next refresh. */}
                    <div className={styles.venmoNotice}>
                      Balance updates after your coach confirms the Venmo payment, usually within a day.
                    </div>
                  </div>
                </div>
              </div>

              {/* Account-zone tiles: Book / Purchase / Recent Activity */}
              <div className={styles.tileGridThree}>
                <button className={styles.tile} onClick={() => setView('booking')}>
                  <div className={styles.tileIcon}>{'📅'}</div>
                  <div className={styles.tileTitle}>Book a Session</div>
                  <div className={styles.tileBody}>Schedule your next training session with Michael. Pay from your balance or by card.</div>
                  <div className={styles.tileCta}>Book Now &rarr;</div>
                </button>

                <button className={styles.tile} onClick={() => setView('credits')}>
                  <div className={styles.tileIcon}>{'💳'}</div>
                  <div className={styles.tileTitle}>Purchase Credits</div>
                  <div className={styles.tileBody}>Keep your account topped up so you never miss a session.</div>
                  <div className={styles.tileCta}>Purchase &rarr;</div>
                </button>

                <button className={styles.tile} onClick={() => setView('transactions')}>
                  <div className={styles.tileIcon}>{'📋'}</div>
                  <div className={styles.tileTitle}>Recent Activity</div>
                  {recentTx.length > 0 ? (
                    <div className={styles.tileTxList}>
                      {recentTx.map(tx => (
                        <div key={tx.id} className={styles.tileTxRow}>
                          <span className={styles.tileTxDate}>{fmtDate(tx.sessionDate || tx.createdAt)}</span>
                          <span className={`${styles.tileTxAmt} ${tx.amount > 0 ? styles.txPos : styles.txNeg}`}>
                            {tx.amount > 0 ? '+' : ''}{fmt(tx.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.tileBody}>No transactions yet.</div>
                  )}
                  <div className={styles.tileCta}>View All &rarr;</div>
                </button>
              </div>

              {/* ════════ ZONE 2 — About [Athlete] (filtered by picker) ════ */}
              {athletes.length > 0 && (
                <>
                  <div className={styles.zoneHeading}>
                    <span className={styles.zoneLabel}>
                      {isAllMode ? 'All Athletes' : `About ${activeAthlete?.name?.split(' ')[0] || 'Athlete'}`}
                    </span>
                  </div>

                  {/* Upcoming Sessions — filtered when a single athlete is selected */}
                  {visibleUpcoming.length > 0 ? (
                    <div className={styles.upcomingCard}>
                      <div className={styles.upcomingHeader}>
                        <div className={styles.upcomingTitle}>Upcoming Sessions</div>
                        <div className={styles.upcomingCount}>
                          {visibleUpcoming.length} scheduled
                          {!isAllMode && upcomingBookings.length !== visibleUpcoming.length
                            ? ` of ${upcomingBookings.length}`
                            : ''}
                        </div>
                      </div>
                      <div className={styles.upcomingList}>
                        {visibleUpcoming.slice(0, 3).map(b => {
                          const d = new Date(b.startAt);
                          const month = d.toLocaleDateString('en-US', { month: 'short' });
                          const day = d.getDate();
                          const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                          const isAccepted = b.status === 'ACCEPTED';
                          const athleteNames = (b.athletes || []).map(a => a.name).filter(Boolean).join(' & ');
                          return (
                            <div key={b.id} className={styles.upcomingRow}>
                              <div className={styles.upcomingDateBlock}>
                                <div className={styles.upcomingMonth}>{month}</div>
                                <div className={styles.upcomingDay}>{day}</div>
                                <div className={styles.upcomingTime}>{time}</div>
                              </div>
                              <div className={styles.upcomingMid}>
                                <div className={styles.upcomingService}>
                                  {b.serviceName || 'Training Session'}
                                </div>
                                <div className={styles.upcomingStaff}>
                                  {b.staffName ? `with ${b.staffName}` : 'with Coach'}
                                  {b.durationMinutes ? ` · ${b.durationMinutes} min` : ''}
                                  {isAllMode && athleteNames ? ` · ${athleteNames}` : ''}
                                </div>
                              </div>
                              <div className={`${styles.upcomingStatus} ${isAccepted ? styles.statusAccepted : styles.statusPending}`}>
                                {isAccepted ? 'Confirmed' : (b.status || '').replace(/_/g, ' ').toLowerCase() || 'Scheduled'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className={styles.upcomingCard}>
                      <div className={styles.upcomingHeader}>
                        <div className={styles.upcomingTitle}>Upcoming Sessions</div>
                      </div>
                      <div className={styles.emptyMsg}>
                        {isAllMode
                          ? 'No upcoming sessions scheduled.'
                          : `No upcoming sessions for ${activeAthlete?.name?.split(' ')[0] || 'this athlete'}.`}
                      </div>
                    </div>
                  )}

                  {/* Progress: single athlete = one full-width tile.
                      All-mode = one mini-tile per athlete in a 2-col grid. */}
                  {isAllMode ? (
                    <div className={styles.tileGridTwo}>
                      {athletes.map(a => {
                        const aLatest = (a.assessments || [])[0];
                        return (
                          <button
                            key={a.id}
                            className={styles.tile}
                            onClick={() => { setView('athletes'); setExpandedAthletes(p => ({ ...p, [a.id]: true })); }}
                          >
                            <div className={styles.tileIcon}>{'🏃'}</div>
                            <div className={styles.tileTitle}>{a.name?.split(' ')[0]}'s Progress</div>
                            {aLatest ? (
                              <div className={styles.tileBody}>
                                Last {aLatest.assessment_type === 'test_day' ? 'Test Day' : 'Evaluation'} — {fmtDate(aLatest.assessment_date)}
                              </div>
                            ) : (
                              <div className={styles.tileBody}>No assessments yet.</div>
                            )}
                            <div className={styles.tileCta}>View Profile &rarr;</div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={styles.tileGridOne}>
                      <button className={styles.tile} onClick={() => { setView('athletes'); if (activeAthleteId) setExpandedAthletes(p => ({ ...p, [activeAthleteId]: true })); }}>
                        <div className={styles.tileIcon}>{'🏃'}</div>
                        <div className={styles.tileTitle}>{activeAthlete?.name?.split(' ')[0]}'s Progress</div>
                        {latestAssessment ? (
                          <div className={styles.tileBody}>
                            Last {latestAssessment.assessment_type === 'test_day' ? 'Test Day' : 'Evaluation'} — {fmtDate(latestAssessment.assessment_date)}
                            {(latestAssessment.scores || []).length > 0 && (
                              <div className={styles.tileScoreRow}>
                                {(latestAssessment.scores || []).slice(0, 3).map(s => (
                                  <span key={s.metric_key || s.id} className={styles.tileScoreChip}>
                                    {s.label}: <strong>{s.value}{s.unit ? ` ${s.unit}` : ''}</strong>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className={styles.tileBody}>No assessments yet.</div>
                        )}
                        <div className={styles.tileCta}>View Full Profile &rarr;</div>
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* ════════ ZONE 3 — Recent Media preview ════════ */}
              {renderMediaPreview()}

              {/* ════════ ZONE 4 — Your Coach (athlete-agnostic) ════════
                * Lives at the bottom because it's a reference / reach-out
                * surface, not a primary action. Same person regardless of
                * which athlete is selected in the picker. Public info pulled
                * from /api/coach-contact; renders nothing if the admin
                * hasn't filled anything in.
                */}
              {hasCoach && (
                <>
                  <div className={styles.zoneHeading}>
                    <span className={styles.zoneLabel}>Your Coach</span>
                  </div>
                  <div className={styles.coachCard}>
                    <div className={styles.coachCardHead}>
                      <div>
                        <div className={styles.coachCardName}>{coach.coachName || 'Your Coach'}</div>
                        {coach.title && <div className={styles.coachCardTitle}>{coach.title}</div>}
                        {coach.businessName && <div className={styles.coachCardBiz}>{coach.businessName}</div>}
                      </div>
                    </div>
                    {coach.message && <p className={styles.coachCardMsg}>{coach.message}</p>}
                    <div className={styles.coachCardLinks}>
                      {coach.phone    && <a href={`tel:${String(coach.phone).replace(/\D/g,'')}`}>📞 {fmtPhone(coach.phone)}</a>}
                      {coach.email    && <a href={`mailto:${coach.email}`}>✉ {coach.email}</a>}
                      {coach.location && <span>📍 {coach.location}</span>}
                      {coach.website  && <a href={String(coach.website).startsWith('http') ? coach.website : `https://${coach.website}`} target="_blank" rel="noopener noreferrer">🌐 {coach.website}</a>}
                      {coach.instagram && <a href={`https://instagram.com/${String(coach.instagram).replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer">◎ {coach.instagram}</a>}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* ══ ATHLETES ══ */}
          {view === 'athletes' && (
            <>
              {athletes.length === 0 && <div className={styles.card}><div className={styles.emptyMsg}>No athletes on file yet.</div></div>}
              {athletes.map(ath => {
                const isOpen = expandedAthletes[ath.id];
                const asmList = ath.assessments || [];
                return (
                  <div key={ath.id} className={styles.athleteCard}>
                    <div className={styles.athleteHeader}>
                      <div className={styles.athleteAvatar}>{ath.name?.charAt(0)}</div>
                      <div>
                        <div className={styles.athleteName}>{ath.name}</div>
                        <div className={styles.athleteMeta}>
                          {[ath.position, ath.grad_year ? `Class of ${ath.grad_year}` : null, ath.team, ath.height, ath.weight ? `${ath.weight} lbs` : null]
                            .filter(Boolean)
                            .map((item, i, arr) => (
                              <span key={i}>
                                <span className={styles.metaChip}>{item}</span>
                                {i < arr.length - 1 && <span className={styles.metaDot}>{'\u00B7'}</span>}
                              </span>
                            ))}
                        </div>
                      </div>
                    </div>

                    <button className={styles.asmToggle} onClick={() => toggleAthlete(ath.id)}>
                      {isOpen ? '\u25BE' : '\u25B8'} Assessments ({asmList.length})
                    </button>

                    {isOpen && (
                      <div className={styles.asmList}>
                        {asmList.length === 0 && <div className={styles.emptyMsg}>No assessments yet.</div>}
                        {asmList.map((asm, asmIdx) => {
                          const scores = asm.scores || [];
                          const strengths = (asm.strengths || []).filter(s => s && s.trim());
                          const weaknesses = (asm.weaknesses || []).filter(w => w && w.trim());
                          const priorBests = buildPriorBests(asmList, asmIdx);
                          return (
                            <div key={asm.id} className={styles.asmCard}>
                              {/* Orange banner like email header */}
                              <div className={styles.asmBanner}>
                                <div className={styles.asmBannerDate}>{fmtDate(asm.assessment_date)}</div>
                                <div className={styles.asmBannerType}>
                                  {asm.assessment_type === 'test_day' ? 'Test Day' : 'Evaluation'}
                                </div>
                              </div>
                              <div className={styles.asmBody}>
                                {/* Dark score tiles with orange glow */}
                                {scores.length > 0 && (
                                  <>
                                    <div className={styles.scoresLabel}>Test Scores</div>
                                    <div className={`${styles.scoresGrid} ${scores.length > 6 ? styles.scoresScroll : ''}`}>
                                      {scores.map(s => {
                                        const prior = priorBests[s.metric_key];
                                        let deltaEl = null;
                                        if (prior !== undefined && s.value != null) {
                                          const current = parseFloat(s.value);
                                          const lib = LOWER_IS_BETTER.has(s.metric_key);
                                          if (prior !== 0 && !isNaN(current) && !isNaN(prior)) {
                                            const rawPct = ((current - prior) / Math.abs(prior)) * 100;
                                            const improved = lib ? current < prior : current > prior;
                                            const displayPct = lib ? -rawPct : rawPct;
                                            const sign = displayPct > 0 ? '+' : '';
                                            deltaEl = (
                                              <>
                                                <div className={`${styles.scoreDelta} ${improved ? styles.deltaUp : styles.deltaDown}`}>
                                                  {improved ? '\u25B2' : '\u25BC'} {sign}{displayPct.toFixed(1)}%
                                                </div>
                                                <div className={styles.scorePrior}>vs best: {prior} {s.unit}</div>
                                              </>
                                            );
                                          }
                                        }
                                        return (
                                          <div key={s.metric_key || s.id} className={styles.scoreTile}>
                                            <div className={styles.scoreEmoji}>{METRIC_EMOJI[s.metric_key] || '\uD83C\uDFC5'}</div>
                                            <div className={styles.scoreLabel}>{s.label}</div>
                                            <div className={styles.scoreValue}>{s.value}</div>
                                            <div className={styles.scoreUnit}>{s.unit}</div>
                                            {deltaEl}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </>
                                )}
                                {/* Strengths card — green border + glow */}
                                {strengths.length > 0 && (
                                  <div className={styles.strengthsCard}>
                                    <div className={styles.strengthsTitle}>Strengths</div>
                                    {strengths.map((s, i) => (
                                      <div key={i} className={styles.bulletItem}>
                                        <span className={`${styles.bulletIcon} ${styles.bulletIconGreen}`}>+</span>
                                        <span>{s}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* Weaknesses card — orange border + glow */}
                                {weaknesses.length > 0 && (
                                  <div className={styles.weaknessesCard}>
                                    <div className={styles.weaknessesTitle}>Areas to Improve</div>
                                    {weaknesses.map((w, i) => (
                                      <div key={i} className={styles.bulletItem}>
                                        <span className={`${styles.bulletIcon} ${styles.bulletIconOrange}`}>&rarr;</span>
                                        <span>{w}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* Coach summary — dark border */}
                                {asm.summary && (
                                  <div className={styles.summaryCard}>
                                    <div className={styles.summaryTitle}>Coach's Summary</div>
                                    <p className={styles.summaryText}>"{asm.summary}"</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* ══ BOOK A SESSION ══ */}
          {view === 'booking' && (
            <BookSession onBooked={onRefresh} />
          )}

          {/* ══ PURCHASE CREDITS ══ */}
          {view === 'credits' && (
            <PurchaseCredits onPurchased={onRefresh} />
          )}

          {/* ══ TRANSACTIONS ══ */}
          {view === 'transactions' && (
            <>
              <div className={styles.card}>
                {transactions.length === 0 && <div className={styles.emptyMsg}>No transactions yet.</div>}
                {transactions.length > 0 && (
                  <>
                    <div className={styles.txHeaderRow}>
                      <span className={styles.txHeaderDate}>Date</span>
                      <span className={styles.txHeaderType}>Type</span>
                      <span className={styles.txHeaderType}>Source</span>
                      <span className={styles.txHeaderDesc}>Description</span>
                      <span className={styles.txHeaderAmt}>Amount</span>
                      <span className={styles.txHeaderBal}>Balance</span>
                    </div>
                    {transactions.map(tx => (
                      <div key={tx.id} className={styles.txRow}>
                        <span className={styles.txCellDate}>{fmtDate(tx.sessionDate || tx.createdAt)}</span>
                        <span className={styles.txCellType}>
                          <span className={`${styles.txTag} ${tx.type === 'credit_load' ? styles.tagCredit : styles.tagDebit}`}>
                            {tx.type === 'credit_load' ? 'Credit' : tx.sessionType || 'Session'}
                          </span>
                        </span>
                        <span className={styles.txCellType}><SourceBadge source={tx.source} /></span>
                        <span className={styles.txCellDesc}>{tx.note}</span>
                        <span className={`${styles.txCellAmt} ${tx.amount > 0 ? styles.txPos : styles.txNeg}`}>
                          {tx.amount > 0 ? '+' : ''}{fmt(tx.amount)}
                        </span>
                        <span className={styles.txCellBal}>{fmt(tx.balanceAfter)}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </>
          )}

          {/* ══ MEDIA ══ */}
          {view === 'media' && (
            <>
              {athletes.length > 1 && (
                <div className={styles.athletePicker} style={{ marginBottom: 12 }}>
                  <button
                    className={`${styles.athletePickerPill} ${isAllMode ? styles.athletePickerActive : ''}`}
                    onClick={() => setSelectedAthleteId('all')}
                  >
                    All
                  </button>
                  {athletes.map(a => (
                    <button
                      key={a.id}
                      className={`${styles.athletePickerPill} ${a.id === activeAthleteId ? styles.athletePickerActive : ''}`}
                      onClick={() => setSelectedAthleteId(a.id)}
                    >
                      {a.name?.split(' ')[0]}
                    </button>
                  ))}
                </div>
              )}

              {mediaLoading && (
                <div className={styles.card}><div className={styles.emptyMsg}>Loading media\u2026</div></div>
              )}

              {!mediaLoading && visibleMedia.length === 0 && (
                <div className={styles.card}>
                  <div className={styles.mediaPlaceholder}>
                    <div className={styles.mediaIcon}>{'\uD83C\uDFAC'}</div>
                    <div className={styles.mediaTitle}>No media yet</div>
                    <p className={styles.mediaText}>
                      Photos and videos your coach shares will appear here.
                    </p>
                  </div>
                </div>
              )}

              {!mediaLoading && visibleMedia.length > 0 && (
                <input
                  className={styles.mediaSearch}
                  value={mediaSearch}
                  onChange={e => setMediaSearch(e.target.value)}
                  placeholder="Search by athlete, caption, date..."
                />
              )}

              {!mediaLoading && visibleMedia.length > 0 && (
                <>
                  {renderMediaSection('Videos', videoPosts)}
                  {renderMediaSection('Photos', photoPosts)}
                </>
              )}
            </>
          )}

        </div>
      </div>

      {playerFile && (
        <VideoOverlay file={playerFile} onClose={() => setPlayer(null)} />
      )}
      {lightbox && (
        <Lightbox
          photos={lightbox.photos}
          index={lightbox.index}
          caption={lightbox.caption}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
